import { useState } from "react";
import { Link } from "react-router-dom";
import { match } from "ts-pattern";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import FrameMockUp from "../../components/FrameMockUp";
import sofaMockUp from "../../assets/images/mock-ups/sofa.webp";
import { formattedPrice } from "../../helpers/money";
import { truncatedTextStyles } from "../../state/constants";
import { colors } from "../../theme";
import { CurrencyCode } from "../../generated/storefront";
import { PrintGroup } from "./useArtQuiz";
import { Frame } from "../../types/product";

const PrintGroupCard = ({ printGroup }: { printGroup: PrintGroup }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const currencyCode = printGroup.products[0].priceRange.minVariantPrice.currencyCode;
  const showDecimals = currencyCode === CurrencyCode.Eur;
  const totalMinPrice = printGroup.products.reduce((acc, product) => acc + Number(product.priceRange.minVariantPrice.amount), 0);

  return (
    <Link to={printGroup.link} target="_blank">
      <Stack gap={{ xs: 1.5, md: 2 }}>
        <Stack direction="row" position="relative" minHeight={{ xs: "45vw", md: "30vw", xl: "25vw", xxl: "10vw" }} width="100%">
          {!imageLoaded && (
            <Skeleton
              width="100%"
              height="100%"
              animation="wave"
              variant="rectangular"
              sx={{ position: "absolute", bgcolor: colors.grey02 }}
            />
          )}
          <img src={sofaMockUp} alt="home mock up" width="100%" height="auto" onLoad={() => setImageLoaded(true)} />
          <Stack
            direction="row"
            gap={{ xs: 1, md: 2 }}
            position="absolute"
            top="26%"
            left="50%"
            width={{ xs: "80%", md: "70%" }}
            padding={{ xs: 2, md: 5 }}
            sx={{ transform: "translate(-50%, -50%)" }}
          >
            {match(printGroup.products.length)
              .with(1, () => {
                const landscape =
                  (printGroup.products[0].images.nodes[0]?.width || 0) > (printGroup.products[0].images.nodes[0]?.height || 0);
                return (
                  <Box
                    key={printGroup.products[0].id}
                    width={landscape ? "75%" : "50%"}
                    marginX="auto"
                    height="100%"
                    position="relative"
                  >
                    <FrameMockUp frame={Frame.Black} image={printGroup.products[0].images.nodes[0]} shadow />
                  </Box>
                );
              })
              .with(2, () => (
                <>
                  <Box key={printGroup.products[0].id} width="50%" height="100%" position="relative">
                    <FrameMockUp frame={Frame.Black} image={printGroup.products[0].images.nodes[0]} shadow />
                  </Box>
                  <Box key={printGroup.products[1].id} width="50%" height="100%" position="relative">
                    <FrameMockUp frame={Frame.Black} image={printGroup.products[1].images.nodes[0]} shadow />
                  </Box>
                </>
              ))
              .with(5, () => (
                <Stack direction="row" gap={{ xs: 0.5, md: 1 }} flexWrap="wrap" justifyContent="center" width="100%">
                  {printGroup.products.map((product, index) => {
                    const width = match(index)
                      .with(0, () => "calc(30% - 8px)")
                      .with(1, () => "calc(35% - 8px)")
                      .with(2, () => "calc(35% - 8px)")
                      .with(3, () => "calc(20% - 8px)")
                      .otherwise(() => "calc(20% - 8px)");
                    const marginTop = match(index)
                      .with(0, () => 2)
                      .with(1, () => 0)
                      .with(2, () => 2)
                      .with(3, () => 0)
                      .otherwise(() => 0);
                    return (
                      <Stack
                        key={product.id}
                        position="relative"
                        width={width}
                        marginTop={marginTop}
                        justifyContent={index < 3 ? "flex-end" : "flex-start"}
                      >
                        <FrameMockUp frame={Frame.Black} image={product.images.nodes[0]} shadow />
                      </Stack>
                    );
                  })}
                </Stack>
              ))
              .otherwise(() => null)}
          </Stack>
        </Stack>
        <Stack gap={0.5} paddingX={{ xs: 2, md: 0 }}>
          <Typography variant="body2" {...truncatedTextStyles}>
            {printGroup.products.map((product) => product.title).join(" · ")}
          </Typography>
          <Stack gap={{ xs: 0.75, md: 1 }} direction="row">
            <Stack direction="row" gap={{ xs: 0.5, md: 0.75 }}>
              <Typography variant="body2">From</Typography>
              <Typography variant="body2" style={{ textDecoration: "line-through" }} color={colors.grey60}>
                {formattedPrice(totalMinPrice, currencyCode, showDecimals)}
              </Typography>
            </Stack>

            <Typography variant="body2" color={colors.red}>
              {formattedPrice(totalMinPrice * (1 - 0.15), currencyCode, showDecimals)} (-15%)
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Link>
  );
};

export default PrintGroupCard;
